import type { ComponentProps } from 'react';
import { SizableText, Spinner, XStack } from 'tamagui';
import { StyledButton, type Variant } from './button.styled';

/**
 * Props for the custom Button component.
 * Extends Tamagui Button props for flexibility.
 */
interface ButtonProps extends Omit<
  ComponentProps<typeof StyledButton>,
  'children'
> {
  /**
   * The visual style variant of the button.
   * - 'primary': Main action button with brand color background.
   * - 'destructive': Red background for dangerous actions like delete.
   * - 'outlined': Transparent background with border for secondary actions.
   * - 'outlined-destructive': Transparent background with destructive color border and text for secondary dangerous actions.
   * - 'ghost': Icon-friendly layout with no border or fill, ideal for tertiary actions like closing modals.
   * - 'ghostBrand': Brand-colored ghost button, ideal for navigation bar actions.
   */
  variant?: Variant;
  /**
   * Optional icon to display on the left side of the button text.
   * Useful for adding visual context (e.g., arrow, trash icon).
   */
  icon?: React.ReactNode;
  /**
   * Disables the button and applies reduced opacity.
   * Prevents interaction and provides visual feedback.
   */
  disabled?: boolean;
  /**
   * Shows a loading spinner instead of children.
   * Replaces button content with spinner during async operations.
   */
  loading?: boolean;
  /**
   * The button's text.
   * Rendered next to the icon if provided.
   */
  title?: string;
}

/**
 * Button component render function.
 * Handles icon positioning, loading state with spinner.
 * Uses XStack for horizontal layout of icon + children.
 * Spinner uses theme color for consistency.
 * Children inherit button typography via StyledButton styles.
 */
export const Button = ({
  variant = 'primary',
  icon,
  loading,
  disabled,
  title,
  onPress,
  width,
  maxWidth,
  flex,
  padding,
  size,
  accessibilityLabel,
}: ButtonProps): React.ReactElement => {
  const variantColor: Record<Variant, string> = {
    primary: '$background',
    destructive: '$background',
    outlined: '$color',
    'outlined-destructive': '$errorFont',
    ghost: '$color',
    ghostBrand: '$brand',
  };

  const content = loading ? (
    <Spinner size="small" color={variantColor[variant]} />
  ) : (
    <XStack alignItems="center" gap="$2">
      {icon}
      {title && (
        <SizableText fontFamily="$bold" color={variantColor[variant]}>
          {title}
        </SizableText>
      )}
    </XStack>
  );

  return (
    <StyledButton
      variant={variant}
      disabled={disabled || loading}
      onPress={onPress}
      width={width}
      maxWidth={maxWidth}
      flex={flex}
      padding={padding}
      size={size}
      accessibilityLabel={accessibilityLabel}
    >
      {content}
    </StyledButton>
  );
};
